import { useState, useEffect, useRef } from "react";

const API_BASE = window.location.port === "3000"
  ? `http://${window.location.hostname}:8000/api`
  : "/api";

const getImgUrl = (path) => {
  if (!path) return null;
  if (path.startsWith("http")) return path;

  const cleanPath = path.startsWith("/") ? path.slice(1) : path;
  const isDev = window.location.port === "3000";
  
  if (isDev) {
    // If running on React Dev Server (port 3000), always try to hit the PHP backend on port 8000
    // We use the current hostname (localhost or IP) to ensure mobile devices can also connect
    return `http://${window.location.hostname}:8000/${cleanPath}`;
  }

  // Production: use current origin
  return `${window.location.origin}/${cleanPath}`;
};

// Student Modal
function StudentModal({ student, onClose }) {
  useEffect(() => { const k = e => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  return (
    <div className="modal-bg" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(520px, 94vw)", borderRadius: 32, overflow: "hidden", background: "#fff", boxShadow: "0 30px 90px rgba(0,0,0,0.25)", padding: "40px 24px", textAlign: "center", zIndex: 1001, maxHeight: "90vh", overflowY: "auto" }}>
        <button onClick={onClose} style={{ position: "absolute", top: 16, right: 16, background: "rgba(0,0,0,0.05)", border: "none", cursor: "pointer", width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "var(--dim)" }}>✕</button>

        <div style={{ width: 84, height: 84, borderRadius: "50%", background: `linear-gradient(135deg,${student.color || 'var(--gold)'},var(--gold-d))`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 32, fontWeight: 800, border: `5px solid #fff`, color: "#fff", boxShadow: "var(--shadow-gold)", margin: "0 auto 20px" }}>{student.initials}</div>

        <div style={{ fontWeight: 800, fontSize: 24, color: "var(--brown)" }}>{student.name}</div>
        <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 4, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700 }}>{student.role}</div>

        <div style={{ marginTop: 28, padding: "28px 20px", background: `${student.color || '#D4AF37'}08`, borderRadius: 24, border: `1.5px solid ${student.color || '#D4AF37'}15`, position: "relative" }}>
          <div style={{ position: "absolute", top: -15, left: "50%", transform: "translateX(-50%)", fontSize: 32 }}>✨</div>
          <p className="serif" style={{ color: "var(--brown)", lineHeight: 1.8, fontSize: 17, fontStyle: "italic" }}>"{student.quote}"</p>
        </div>

        <div style={{ marginTop: 32, display: "flex", justifyContent: "center", gap: 10, flexWrap: "wrap" }}>
          <button className="btn-rose" style={{ padding: "12px 24px", borderRadius: 50, fontSize: 13 }}>🤍 Like</button>
          <button className="btn-ghost" style={{ padding: "12px 24px", borderRadius: 50, fontSize: 13 }}>🔗 Share</button>
        </div>
      </div>
    </div>
  );
}

const FONTS = `@import url('https://fonts.googleapis.com/css2?family=Dancing+Script:wght@400;600;700&family=Lora:ital,wght@0,400;0,600;1,400;1,600&family=Nunito:wght@300;400;500;600;700&display=swap');`;

const CSS = `
*{box-sizing:border-box;margin:0;padding:0;}
:root{
  --gold:#D4AF37; --gold-l:#F9F2D8; --gold-d:#9E8226;
  --grey:#9CA3AF; --grey-l:#E5E7EB; --grey-d:#4B5563;
  --black:#111827; --black-l:#374151; --black-d:#000000;
  --white:#FFFFFF; --white-d:#F8FAFC;
  --text:var(--black); --muted:var(--grey-d); --dim:var(--grey);
  --shadow-gold: 0 12px 40px rgba(212,175,55,.2);
  --shadow-grey: 0 12px 40px rgba(156,163,175,.15);
  --shadow-soft: 0 15px 45px rgba(17,24,39,.06);
  --shadow-glow: 0 0 30px rgba(212,175,55,.25);
  --glass: rgba(255, 255, 255, 0.7);
  --glass-border: rgba(212, 175, 55, 0.2);
}
html{scroll-behavior:smooth;}
body{background:var(--white-d);color:var(--text);font-family:'Nunito',sans-serif;overflow-x:hidden;}
.script{font-family:'Dancing Script',cursive;}
.serif{font-family:'Lora',serif;}
::-webkit-scrollbar{width:8px;}
::-webkit-scrollbar-track{background:var(--white);}
::-webkit-scrollbar-thumb{background:linear-gradient(180deg, var(--grey-l), var(--gold-l));border-radius:4px;}
 
@keyframes floatUp{0%{transform:translateY(0) rotate(0deg);opacity:1}100%{transform:translateY(-150px) rotate(25deg);opacity:0}}
@keyframes sway{0%,100%{transform:translateY(0) rotate(-3deg)}50%{transform:translateY(-15px) rotate(3deg)}}
@keyframes fadeIn{from{opacity:0;transform:translateY(30px)}to{opacity:1;transform:translateY(0)}}
@keyframes heartbeat{0%,100%{transform:scale(1)}50%{transform:scale(1.2)}}
@keyframes wiggle{0%,100%{transform:rotate(-4deg)}50%{transform:rotate(4deg)}}
@keyframes ripple{0%{transform:scale(1);opacity:0.8}50%{transform:scale(1.05);opacity:1}100%{transform:scale(1);opacity:0.8}}
@keyframes aurora{0%,100%{background-position:0% 50%}50%{background-position:100% 50%}}
@keyframes popIn{0%{transform:scale(0) rotate(-15deg)}70%{transform:scale(1.1) rotate(3deg)}100%{transform:scale(1) rotate(0deg)}}
@keyframes shine{0%{left:-100%}100%{left:100%}}
@keyframes goldPulse{0%{box-shadow: 0 0 0 0 rgba(212,175,55,0.4)}70%{box-shadow: 0 0 0 15px rgba(212,175,55,0)}100%{box-shadow: 0 0 0 0 rgba(212,175,55,0)}}
@keyframes slideInUp{from{opacity:0;transform:translateY(50px)}to{opacity:1;transform:translateY(0)}}
 
.fadeIn{animation:fadeIn .8s ease both;}
.sway{animation:sway 5s ease-in-out infinite;}
.heartbeat{animation:heartbeat 2.5s ease-in-out infinite;}
 
/* Realistic polaroid with gold accent */
.polaroid{
  background:#ffffff;
  padding:16px 16px 52px;
  box-shadow:0 12px 30px rgba(0,0,0,.08), 0 4px 10px rgba(0,0,0,.04);
  border:1px solid rgba(212,175,55,.1);
  position:relative;
  transition:all .4s cubic-bezier(.25,.8,.25,1);
  cursor:pointer;
  z-index: 1;
}
.polaroid::after{content:'';position:absolute;inset:0;background:linear-gradient(135deg, rgba(255,255,255,.6), transparent);pointer-events:none;}
.polaroid:hover{transform:translateY(-12px) scale(1.03) rotate(1deg) !important;box-shadow:0 30px 70px rgba(0,0,0,.15), var(--shadow-glow);border-color:var(--gold);z-index: 10;}
.polaroid::before{content:'';position:absolute;top:0;left:-100%;width:50%;height:100%;background:linear-gradient(90deg, transparent, rgba(255,255,255,0.3), transparent);transform:skewX(-25deg);transition:0.5s;z-index:2;}
.polaroid:hover::before{animation:shine 0.8s forwards;}
.polaroid-img{
  width:100%;
  aspect-ratio:1;
  object-fit:cover;
  background:#f8f9fa;
  box-shadow:inset 0 2px 10px rgba(0,0,0,.05);
  position:relative;
  border:1px solid rgba(0,0,0,.03);
}
.polaroid-img::before{content:'';position:absolute;inset:0;background:linear-gradient(180deg, rgba(255,255,255,.15) 0%, transparent 20%, transparent 80%, rgba(0,0,0,.05) 100%);pointer-events:none;}
.polaroid-caption{font-family:'Dancing Script',cursive;font-size:20px;color:var(--black);text-align:center;margin-top:18px;font-weight:600;}
 
/* Sophisticated tape */
.tape{position:absolute;top:-12px;left:50%;transform:translateX(-50%);width:70px;height:24px;background:rgba(255,255,255,.4);backdrop-filter:blur(4px);border:1px solid rgba(0,0,0,.05);box-shadow:0 2px 5px rgba(0,0,0,.03);}
 
/* Chips */
.chip{padding:10px 24px;border-radius:50px;font-size:14px;font-weight:600;cursor:pointer;transition:all .3s;border:1px solid var(--grey-l);background:#fff;color:var(--grey-d);font-family:'Nunito',sans-serif;box-shadow:0 2px 8px rgba(0,0,0,.02);}
.chip.active{background:var(--gold);color:#fff;border-color:var(--gold);box-shadow:var(--shadow-gold);}
.chip:hover:not(.active){border-color:var(--gold);color:var(--gold-d);}
 
/* Nav pill */
.nav-pill{padding:10px 20px;border-radius:50px;font-size:14px;font-weight:600;cursor:pointer;transition:all .3s;background:transparent;border:1px solid transparent;font-family:'Nunito',sans-serif;color:var(--grey-d);}
.nav-pill:hover,.nav-pill.active{background:rgba(212,175,55,.05);color:var(--gold-d);border-color:rgba(212,175,55,.1);}
 
/* Premium Card */
.story-card{background:#ffffff;border-radius:16px;box-shadow:0 8px 30px rgba(0,0,0,.04);transition:all .4s;border:1px solid rgba(212,175,55,.1);position:relative;overflow:hidden;}
.story-card:hover{transform:translateY(-6px);box-shadow:0 16px 40px rgba(0,0,0,.08);border-color:var(--gold);}
.story-card::before{content:'';position:absolute;top:0;left:0;right:0;height:3px;background:linear-gradient(90deg, var(--gold-l), var(--gold), var(--black));}
 
/* Progress */
.progress{height:4px;border-radius:4px;background:var(--grey-l);overflow:hidden;}
.progress-fill{height:100%;border-radius:4px;background:linear-gradient(90deg,var(--gold-l), var(--gold), var(--black));animation:aurora 3s linear infinite;}
 
/* Drop zone */
.drop-zone{border:2px dashed var(--gold);border-radius:16px;padding:60px 40px;text-align:center;cursor:pointer;transition:all .4s;background:rgba(212,175,55,.02);}
.drop-zone:hover,.drop-zone.over{background:rgba(212,175,55,.05);transform:scale(1.02);}
 
/* Buttons */
.btn-rose{background:linear-gradient(135deg,var(--gold), var(--gold-d));color:#fff;border:none;cursor:pointer;transition:all .3s;font-family:'Nunito',sans-serif;font-weight:700;border-radius:50px;}
.btn-rose:hover{filter:brightness(1.1);transform:translateY(-2px);box-shadow:var(--shadow-gold);}
.btn-amber{background:var(--black);color:#fff;border:none;cursor:pointer;transition:all .3s;font-family:'Nunito',sans-serif;font-weight:700;border-radius:50px;}
.btn-amber:hover{background:var(--black-l);transform:translateY(-2px);box-shadow:0 8px 20px rgba(0,0,0,.2);}
.btn-ghost{background:transparent;border:1.5px solid var(--grey);color:var(--black);cursor:pointer;transition:all .3s;font-family:'Nunito',sans-serif;font-weight:600;border-radius:50px;}
.btn-ghost:hover{border-color:var(--gold);color:var(--gold-d);background:rgba(212,175,55,.05);transform:translateY(-2px);}
 
/* Divider */
.divider{display:flex;align-items:center;gap:16px;margin:16px 0;opacity:0.6;}
.divider::before,.divider::after{content:'';flex:1;height:1px;background:linear-gradient(90deg, transparent, var(--gold), transparent);}
.divider::before{position:relative;}
.divider::before::after{content:'✨';position:absolute;left:50%;top:-9px;transform:translateX(-50%);font-size:12px;color:var(--gold);}
 
/* Responsive Base */
.upload-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 32px; }
.gallery-grid { columns: 4 180px; gap: 24px; }
.timeline-container { position: relative; }
.timeline-line { position: absolute; left: 50%; top: 0; bottom: 0; width: 3px; background: linear-gradient(to bottom,rgba(212,175,55,0),var(--gold),var(--gold-d),rgba(212,175,55,0)); transform: translateX(-50%); border-radius: 2px; }
.timeline-item { display: flex; margin-bottom: 52px; position: relative; }
.timeline-item.right { justify-content: flex-end; padding-left: 52%; }
.timeline-item.left { justify-content: flex-start; padding-right: 52%; }
.timeline-dot { position: absolute; left: 50%; top: 22px; transform: translateX(-50%); z-index: 2; }
.nav-container { position: fixed; top: 0; left: 0; right: 0; z-index: 100; height: 68px; display: flex; align-items: center; justify-content: space-between; padding: 0 28px; background: rgba(255,255,255,.92); backdrop-filter: blur(16px); border-bottom: 1.5px solid rgba(212,175,55,.12); box-shadow: 0 2px 20px rgba(0,0,0,.08); }
.nav-links { display: flex; gap: 4px; align-items: center; }
.floating-polaroid { position: absolute; z-index: 1; }

.hamburger { display: none; background: none; border: none; font-size: 24px; cursor: pointer; color: var(--gold-d); padding: 8px; margin-left: auto; }

@media (max-width: 768px) {
  .nav-container { padding: 0 16px; }
  .nav-links { display: none; position: absolute; top: 68px; left: 0; right: 0; background: rgba(255,255,255,0.98); backdrop-filter: blur(16px); flex-direction: column; padding: 24px; box-shadow: 0 10px 30px rgba(0,0,0,0.1); border-top: 1px solid rgba(212,175,55,0.1); border-bottom: 1px solid rgba(212,175,55,0.1); align-items: stretch; }
  .nav-links.open { display: flex; }
  .nav-pill { padding: 12px 24px; font-size: 16px; width: 100%; text-align: center; border-radius: 12px; }
  .nav-links .btn-rose { display: block; width: 100%; text-align: center; margin: 12px 0 0 0 !important; }
  .hamburger { display: block; }
  main { padding-bottom: 0; }
  .gallery-grid { columns: 1; gap: 24px; }
  .upload-grid { grid-template-columns: 1fr; gap: 24px; }
  .timeline-line { left: 24px; }
  .timeline-item.left, .timeline-item.right { justify-content: flex-start; padding-left: 64px; padding-right: 0; }
  .timeline-dot { left: 24px; }
  .floating-polaroid { display: none; }
}

/* Modal */
.modal-bg{position:fixed;inset:0;background:rgba(255,255,255,.8);z-index:1000;backdrop-filter:blur(12px);}
.modal-bg::before{content:'';position:absolute;inset:0;background:radial-gradient(circle at center, rgba(212,175,55,0.05), transparent 70%);}

.glass-card { background: rgba(255, 255, 255, 0.7); backdrop-filter: blur(12px); border: 1.5px solid rgba(212, 175, 55, 0.2); box-shadow: var(--shadow-soft); transition: all 0.4s cubic-bezier(0.175, 0.885, 0.32, 1.275); }
.glass-card:hover { transform: translateY(-10px); border-color: var(--gold); box-shadow: var(--shadow-glow); }

.pulse-gold { animation: goldPulse 2s infinite; }
.reveal { animation: slideInUp 1s cubic-bezier(0.23, 1, 0.32, 1) both; }

@keyframes loader-logo { 0%, 100% { transform: scale(1) rotate(0deg); opacity: 0.8; } 50% { transform: scale(1.15) rotate(5deg); opacity: 1; } }
@keyframes line-glow { 0%, 100% { width: 0%; left: 50%; opacity: 0; } 50% { width: 100%; left: 0%; opacity: 1; } }
@keyframes shimmer-fast { 0% { background-position: -200% 0; } 100% { background-position: 200% 0; } }

.preloader-bg {
  position: fixed; inset: 0; z-index: 9999;
  background: radial-gradient(circle at center, #ffffff 0%, #f8f9fa 100%);
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  transition: opacity 0.8s cubic-bezier(0.65, 0, 0.35, 1);
}
.loader-symbol {
  width: 100px; height: 100px; margin-bottom: 40px;
  background: linear-gradient(135deg, var(--gold-l), var(--gold), var(--gold-d));
  -webkit-background-clip: text; -webkit-text-fill-color: transparent;
  font-size: 64px; display: flex; align-items: center; justify-content: center;
  animation: loader-logo 3s ease-in-out infinite;
  filter: drop-shadow(0 10px 20px rgba(212,175,55,0.2));
}
.loader-text {
  text-align: center;
}
.loader-line-container {
  width: 240px; height: 2px; background: rgba(156,163,175,0.1);
  margin: 24px auto; position: relative; overflow: hidden;
}
.loader-line {
  position: absolute; height: 100%;
  background: linear-gradient(90deg, transparent, var(--gold), transparent);
  animation: line-glow 2.5s ease-in-out infinite;
}
`;

const TAPES = ["rgba(255,255,255,0.6)", "rgba(212,175,55,0.2)", "rgba(0,0,0,0.1)", "rgba(212,175,55,0.15)", "rgba(255,255,255,0.7)", "rgba(107,114,128,0.15)"];
const ROTS = [-3, -1, -2, 2, 3, -1, 2, -1, 1, -2, 1, -3, 2, -1, 2];
// Note: All data is now managed in App state via API

const FILTERS = ["All ✨", "Photos 📸", "Videos 🎥", "Stage Events 💃", "Candid ✨", "Group Photos 👥", "Teachers 🎓", "Reels 🎬"];
const CHIP_COLORS = ["var(--gold)", "var(--black)", "var(--grey)", "var(--gold)", "var(--black)", "var(--grey)", "var(--gold)", "var(--black)"];

// ─── COMPONENTS ───────────────────────────────────────────────────────────────
function Preloader() {
  return (
    <div className="preloader-bg" style={{ background: "#0a0a0a" }}>
      <div style={{ position: "absolute", inset: 0, opacity: 0.1, background: "url('https://www.transparenttextures.com/patterns/black-paper.png')" }} />
      <div className="loader-symbol" style={{ fontSize: 80 }}>✦</div>
      <div className="loader-text" style={{ position: "relative", zIndex: 2 }}>
        <h2 className="script" style={{ fontSize: 56, color: "var(--gold)", marginBottom: 8, letterSpacing: 3, filter: "drop-shadow(0 0 10px rgba(212,175,55,0.3))" }}>Farewell</h2>
        <p className="serif" style={{ fontSize: 13, color: "var(--gold-l)", textTransform: "uppercase", letterSpacing: 6, fontWeight: 700, opacity: 0.8 }}>The Batch of 2026</p>
        <div className="loader-line-container" style={{ width: 280, height: 3, background: "rgba(212,175,55,0.1)", marginTop: 32 }}>
          <div className="loader-line" style={{ background: "linear-gradient(90deg, transparent, var(--gold-l), var(--gold), var(--gold-l), transparent)" }}></div>
        </div>
        <p className="serif" style={{ fontSize: 12, color: "#9CA3AF", fontStyle: "italic", marginTop: 16, letterSpacing: 1 }}>Gathering your golden moments...</p>
      </div>
      <div style={{ position: "absolute", bottom: 40, fontSize: 10, color: "rgba(212,175,55,0.4)", letterSpacing: 2, fontWeight: 700 }} className="serif">DEVELOPED BY RASHID</div>
    </div>
  );
}

// Floating heritage elements
function Hearts() {
  const [elements, setElements] = useState([]);
  useEffect(() => {
    const emojis = ["✦", "✧", "✨", "🔱", "⚜️", "🔱", "⚜️", "✨", "✦"];
    const arr = Array.from({ length: 25 }, (_, i) => ({
      id: i, x: Math.random() * 100, delay: Math.random() * 15,
      dur: 10 + Math.random() * 12, size: 8 + Math.random() * 20,
      emoji: emojis[Math.floor(Math.random() * emojis.length)],
      anim: Math.random() > 0.4 ? 'floatUp' : 'sway'
    }));
    setElements(arr);
  }, []);
  return (
    <div style={{ position: "fixed", inset: 0, pointerEvents: "none", zIndex: 0, overflow: "hidden" }}>
      <div style={{ position: "absolute", inset: 0, background: "radial-gradient(circle at 50% 50%, rgba(212,175,55,0.03) 0%, transparent 70%)" }} />
      {elements.map(h => (
        <div key={h.id} style={{
          position: "absolute", left: `${h.x}%`, bottom: -60,
          fontSize: h.size, opacity: .35,
          color: h.id % 2 === 0 ? "var(--gold)" : "var(--grey)",
          animation: `${h.anim} ${h.dur}s ${h.delay}s linear infinite`,
          filter: 'drop-shadow(0 0 10px rgba(212,175,55,0.4))',
          textShadow: "0 0 5px rgba(255,255,255,0.8)"
        }}>{h.emoji}</div>
      ))}
    </div>
  );
}

// Nav
function Nav({ page, setPage, isAdmin }) {
  const [open, setOpen] = useState(false);
  const links = [
    { id: "home", l: "Home" },
    { id: "gallery", l: "Gallery" },
    { id: "videos", l: "Videos" },
    { id: "wall", l: "Students" }
  ];
  if (isAdmin) links.push({ id: "admin", l: "Admin Dashboard" });

  return (
    <nav className="nav-container">
      <div onClick={() => { setPage("home"); setOpen(false); }} style={{ cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: "50%", background: "linear-gradient(135deg,var(--gold),var(--grey))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, boxShadow: "0 4px 12px rgba(212,175,55,.25)" }}>✨</div>
        <span className="script" style={{ fontSize: 22, color: "var(--gold-d)", fontWeight: 700 }}>Farewell <span style={{ color: "var(--grey-d)" }}>2026</span></span>
      </div>
      <button className="hamburger" onClick={() => setOpen(!open)}>{open ? "✕" : "☰"}</button>
      <div className={`nav-links ${open ? "open" : ""}`}>
        {links.map(l => (
          <button key={l.id} className={`nav-pill ${page === l.id ? "active" : ""}`} onClick={() => { setPage(l.id); setOpen(false); }}>{l.l}</button>
        ))}
        {!isAdmin && <button className="nav-pill" style={{ opacity: 0.5, fontSize: 10 }} onClick={() => setPage("login")}>Admin</button>}
        <button className="btn-rose" style={{ padding: "9px 22px", borderRadius: 50, fontSize: 13, marginLeft: 8 }} onClick={() => { setPage("gallery"); setOpen(false); }}>✦ Memories</button>
      </div>
    </nav>
  );
}

// Lightbox
function Lightbox({ photo, onClose }) {
  useEffect(() => { const k = e => e.key === "Escape" && onClose(); window.addEventListener("keydown", k); return () => window.removeEventListener("keydown", k); }, [onClose]);
  return (
    <div className="modal-bg" onClick={onClose}>
      <div onClick={e => e.stopPropagation()} style={{ position: "fixed", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(620px, 94vw)", borderRadius: 24, overflow: "hidden", background: "#fff", boxShadow: "0 24px 80px rgba(0,0,0,.25)", maxHeight: "92vh", display: "flex", flexDirection: "column", zIndex: 1001 }}>
        <div style={{ height: "min(450px, 50vh)", background: photo.image_path ? `#000 url(${getImgUrl(photo.image_path)}) center/contain no-repeat` : photo.grad, display: "flex", alignItems: "center", justifyContent: "center", position: "relative", flexShrink: 0 }}>
          {!photo.image_path && <span style={{ fontSize: 56, filter: "drop-shadow(0 4px 12px rgba(0,0,0,.2))" }}>🖼️</span>}
          <div style={{ position: "absolute", top: 12, right: 12, zIndex: 10 }}>
            <span style={{ background: "rgba(255,255,255,0.9)", padding: "4px 14px", borderRadius: 50, fontSize: 10, fontWeight: 800, color: "var(--brown)", boxShadow: "0 2px 10px rgba(0,0,0,0.1)" }}>{photo.type}</span>
          </div>
        </div>
        <div style={{ padding: "24px 28px", background: "#fff", overflowY: "auto" }}>
          <div className="script" style={{ fontSize: 24, color: "var(--brown)", marginBottom: 4 }}>{photo.label}</div>
          <div style={{ color: "var(--muted)", fontSize: 13, marginBottom: 20 }}>📷 {photo.uploader} · {photo.date}</div>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
            <button className="btn-rose" style={{ padding: "10px 24px", borderRadius: 50, fontSize: 13 }}>🤍 Like</button>
            <button className="btn-ghost" style={{ padding: "10px 24px", borderRadius: 50, fontSize: 13 }} onClick={() => window.open(getImgUrl(photo.image_path), "_blank")}>⬇ Save</button>
            <button onClick={onClose} style={{ marginLeft: "auto", background: "rgba(0,0,0,0.05)", border: "none", cursor: "pointer", width: 40, height: 40, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, color: "var(--muted)" }}>✕</button>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── HOME ─────────────────────────────────────────────────────────────────────
function Home({ setPage, PHOTOS, FACULTY, VIDEOS, STUDENTS }) {
  return (
    <div style={{ position: "relative" }}>
      {/* HERO */}
      <section style={{ minHeight: "100vh", display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", textAlign: "center", padding: "90px 24px 60px", position: "relative" }}>
        <div style={{ animation: "popIn .8s ease .1s both" }}>
          <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: "rgba(212,175,55,.08)", border: "1.5px solid rgba(212,175,55,.2)", borderRadius: 50, padding: "10px 28px", marginBottom: 28, backdropFilter: "blur(8px)", boxShadow: "var(--shadow-glow)" }}>
            <span style={{ animation: "wiggle 2s infinite" }}>🔱</span>
            <span style={{ fontSize: 12, fontWeight: 800, color: "var(--gold-d)", letterSpacing: 2, textTransform: "uppercase" }}>CSBS Department  · Batch 2022 – 2026</span>
            <span style={{ animation: "wiggle 2s infinite reverse" }}>🔱</span>
          </div>
        </div>

        <h1 className="script" style={{ fontSize: "clamp(52px,9vw,100px)", color: "var(--gold-d)", lineHeight: 0.95, animation: "fadeIn .8s ease .2s both", marginBottom: 16, textShadow: "0 10px 30px rgba(0,0,0,0.05)" }}>
          Four Golden Years of<br />
          <span style={{ color: "var(--black)", position: "relative" }}>
            Beautiful Chaos
            <span style={{ position: "absolute", bottom: 10, left: 0, width: "100%", height: 8, background: "var(--gold-l)", zIndex: -1, opacity: 0.6 }} />
          </span>
        </h1>
        <h2 className="serif" style={{ fontSize: "clamp(20px,3.8vw,40px)", color: "var(--grey-d)", fontStyle: "italic", fontWeight: 400, animation: "fadeIn .8s ease .4s both", marginBottom: 32, letterSpacing: 1 }}>
          A Legacy of <span style={{ color: "var(--gold-d)", fontWeight: 600, borderBottom: "2px solid var(--gold)" }}>Unforgettable Memories</span>
        </h2>
        <p className="serif" style={{ fontSize: "clamp(16px,2vw,21px)", color: "var(--muted)", maxWidth: 650, lineHeight: 1.9, animation: "fadeIn .8s ease .55s both", marginBottom: 52, fontStyle: "italic", opacity: 0.9 }}>
          "Time flies, but these moments are frozen in gold. Every laugh, every challenge, every shared dream — preserved forever in our digital sanctuary."
        </p>
        <div style={{ display: "flex", gap: 16, flexWrap: "wrap", justifyContent: "center", animation: "fadeIn .8s ease .7s both" }}>
          <button className="btn-rose" style={{ padding: "15px 40px", borderRadius: 50, fontSize: 15 }} onClick={() => setPage("gallery")}>🤍 Open Our Gallery</button>
        </div>

        {/* Floating polaroid previews */}
        {PHOTOS.length > 0 && (
          <>
            <div className="floating-polaroid" style={{ left: "3%", top: "22%", transform: "rotate(-8deg)", animation: "sway 5s ease-in-out infinite" }}>
              <div className="polaroid" style={{ width: 120, transform: "rotate(-8deg)" }}>
                <div className="polaroid-img" style={{ height: 90, background: PHOTOS[0].image_path ? `url(${getImgUrl(PHOTOS[0].image_path)}) center/cover` : PHOTOS[0].grad, borderRadius: 1 }} />
                <div className="polaroid-caption" style={{ fontSize: 11 }}>{PHOTOS[0].label || "Memory"} ✨</div>
              </div>
            </div>
            {PHOTOS.length > 4 && (
              <div className="floating-polaroid" style={{ right: "3%", top: "28%", animation: "sway 6s ease-in-out infinite .7s" }}>
                <div className="polaroid" style={{ width: 120, transform: "rotate(6deg)" }}>
                  <div className="polaroid-img" style={{ height: 90, background: PHOTOS[4].image_path ? `url(${getImgUrl(PHOTOS[4].image_path)}) center/cover` : PHOTOS[4].grad, borderRadius: 1 }} />
                  <div className="polaroid-caption" style={{ fontSize: 11 }}>{PHOTOS[4].label || "Memory"} ✨</div>
                </div>
              </div>
            )}
            {PHOTOS.length > 2 && (
              <div className="floating-polaroid" style={{ left: "8%", bottom: "18%", animation: "sway 7s ease-in-out infinite 1s" }}>
                <div className="polaroid" style={{ width: 100, transform: "rotate(4deg)" }}>
                  <div className="polaroid-img" style={{ height: 78, background: PHOTOS[2].image_path ? `url(${getImgUrl(PHOTOS[2].image_path)}) center/cover` : PHOTOS[2].grad, borderRadius: 1 }} />
                  <div className="polaroid-caption" style={{ fontSize: 10 }}>{PHOTOS[2].label || "Memory"} 💜</div>
                </div>
              </div>
            )}
            {PHOTOS.length > 1 && (
              <div className="floating-polaroid" style={{ right: "7%", bottom: "22%", animation: "sway 5.5s ease-in-out infinite .3s" }}>
                <div className="polaroid" style={{ width: 100, transform: "rotate(-5deg)" }}>
                  <div className="polaroid-img" style={{ height: 78, background: PHOTOS[1].image_path ? `url(${getImgUrl(PHOTOS[1].image_path)}) center/cover` : PHOTOS[1].grad, borderRadius: 1 }} />
                  <div className="polaroid-caption" style={{ fontSize: 10 }}>{PHOTOS[1].label || "Memory"} ⚡</div>
                </div>
              </div>
            )}
          </>
        )}
      </section>

      {/* STATS */}
      <section className="reveal" style={{ padding: "60px 24px", background: "linear-gradient(180deg,transparent,rgba(212,175,55,0.03),transparent)", borderTop: "1.5px solid rgba(212,175,55,.12)", borderBottom: "1.5px solid rgba(212,175,55,.12)" }}>
        <div style={{ maxWidth: 960, margin: "0 auto", display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))", gap: 24 }}>
          {[
            { v: PHOTOS.length, l: "Photographs", icon: "📸", c: "var(--gold)" },
            { v: VIDEOS.length, l: "Videos & Reels", icon: "🎬", c: "var(--black)" },
            { v: PHOTOS.length + VIDEOS.length, l: "Memories Made", icon: "🤍", c: "var(--gold)" },
            { v: "4", l: "Golden Years", icon: "🌟", c: "var(--black)" }
          ].map((s, i) => (
            <div key={i} className="glass-card" style={{ borderRadius: 24, padding: "32px 24px", textAlign: "center" }}>
              <div style={{ fontSize: 36, marginBottom: 12 }}>{s.icon}</div>
              <div className="script" style={{ fontSize: 52, color: s.c, lineHeight: 1, marginBottom: 6 }}>{s.v}</div>
              <div style={{ color: "var(--muted)", fontSize: 14, fontWeight: 700, letterSpacing: 1, textTransform: "uppercase" }}>{s.l}</div>
            </div>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section style={{ padding: "72px 24px", maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 56 }}>
          <div className="divider"><span className="script" style={{ fontSize: 14, color: "var(--muted)", whiteSpace: "nowrap" }}>✦ ✦ ✦</span></div>
          <h2 className="script" style={{ fontSize: "clamp(36px,5vw,56px)", color: "var(--rose-d)", marginTop: 16 }}>A Peek Into Our Story</h2>
          <p className="serif" style={{ color: "var(--muted)", marginTop: 12, fontStyle: "italic", fontSize: 16 }}>Stolen moments, genuine smiles, and memories that shimmer like gold</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(min(100%, 280px), 1fr))", gap: 28, justifyItems: "center" }}>
          {PHOTOS.slice(0, 8).map((p, i) => (
            <div key={i} className="polaroid" style={{ width: "100%", maxWidth: 220, transform: `rotate(${ROTS[i]}deg)`, cursor: "pointer" }} onClick={() => setPage("gallery")}>
              <div className="tape" style={{ background: TAPES[i % TAPES.length], left: "50%", transform: "translateX(-50%)" }} />
              <div style={{ height: 180, background: p.image_path ? `url(${getImgUrl(p.image_path)}) center/cover` : p.grad, borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 28, opacity: p.image_path ? 1 : .6 }}>
                {!p.image_path && "🖼️"}
              </div>
              <div className="polaroid-caption">{p.label}</div>
              <div style={{ textAlign: "center", fontSize: 11, color: "var(--dim)", marginTop: 4, fontFamily: "'Nunito',sans-serif" }}>{p.date}</div>
            </div>
          ))}
        </div>
        <div style={{ textAlign: "center", marginTop: 48 }}>
          <button className="btn-amber" style={{ padding: "14px 40px", borderRadius: 50, fontSize: 15 }} onClick={() => setPage("gallery")}>View All 412 Memories ✨</button>
        </div>
      </section>

      {/* STUDENT MESSAGES */}
      <section style={{ padding: "72px 24px", background: "linear-gradient(135deg,rgba(156,163,175,.08),rgba(212,175,55,.06))", borderTop: "1.5px solid rgba(156,163,175,.15)", borderBottom: "1.5px solid rgba(156,163,175,.15)" }}>
        <div style={{ maxWidth: 1100, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <div className="divider"><span className="script" style={{ fontSize: 14, color: "var(--muted)", whiteSpace: "nowrap" }}>✦ ✦ ✦</span></div>
            <h2 className="script" style={{ fontSize: "clamp(32px,4.5vw,52px)", color: "var(--rose-d)", marginTop: 16 }}>Voices of the Heart</h2>
            <p className="serif" style={{ color: "var(--muted)", fontStyle: "italic", marginTop: 10 }}>Every voice of the Class of 2026 — written from the heart</p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(min(100%, 350px), 1fr))", gap: 32 }}>
            {STUDENTS.slice(0, 3).map((s, i) => (
              <div key={i} className="glass-card reveal" style={{ padding: "36px 32px", borderRadius: 28, position: "relative", overflow: "visible", cursor: "pointer" }} onClick={() => setPage("wall")}>
                <div style={{ position: "absolute", top: -18, left: 32, width: 40, height: 40, borderRadius: "50%", background: "linear-gradient(135deg,var(--gold-l),var(--gold))", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, boxShadow: "0 5px 15px rgba(212,175,55,.3)", color: "#fff" }}>“</div>
                <div style={{ display: "flex", alignItems: "center", gap: 16, marginBottom: 24 }}>
                  <div style={{ width: 58, height: 58, borderRadius: "50%", background: `linear-gradient(135deg,${s.color},var(--black))`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20, fontWeight: 800, border: `3px solid #fff`, flexShrink: 0, color: "#fff", boxShadow: "var(--shadow-soft)" }}>{s.initials}</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: "var(--brown)" }}>{s.name}</div>
                    <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{s.role}</div>
                  </div>
                </div>
                <p className="serif" style={{ color: "var(--muted)", lineHeight: 1.8, fontSize: 15, fontStyle: "italic" }}>"{s.quote.length > 120 ? s.quote.substring(0, 120) + "..." : s.quote}"</p>
              </div>
            ))}
          </div>
          <div style={{ textAlign: "center", marginTop: 44 }}>
            <button className="btn-amber" style={{ padding: "14px 40px", borderRadius: 50, fontSize: 15 }} onClick={() => setPage("wall")}>Open Student Wall ✨</button>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section style={{ padding: "100px 24px", textAlign: "center" }}>
        <div style={{ maxWidth: 620, margin: "0 auto" }}>
          <div style={{ fontSize: 48, marginBottom: 20, animation: "heartbeat 2s infinite" }}>🤍</div>
          <h2 className="script" style={{ fontSize: "clamp(30px,4.5vw,52px)", color: "var(--rose-d)", marginBottom: 16 }}>Ready to Relive the Magic?</h2>
          <p className="serif" style={{ color: "var(--muted)", fontSize: 16, fontStyle: "italic", marginBottom: 36, lineHeight: 1.8 }}>Every photograph holds a story. Every video holds a feeling. Open the gallery and let the memories flood back.</p>
          <div style={{ display: "flex", gap: 16, justifyContent: "center", flexWrap: "wrap" }}>
            <button className="btn-rose" style={{ padding: "15px 40px", borderRadius: 50, fontSize: 15 }} onClick={() => setPage("gallery")}>🤍 Open Gallery</button>
            <button className="btn-ghost" style={{ padding: "15px 40px", borderRadius: 50, fontSize: 15 }} onClick={() => setPage("wall")}>✨ Student Wall</button>
          </div>
        </div>
      </section>
    </div>
  );
}

// ─── GALLERY ──────────────────────────────────────────────────────────────────
function Gallery({ PHOTOS, setLb }) {
  const [filter, setFilter] = useState("All ✨");
  const [search, setSearch] = useState("");
  const [liked, setLiked] = useState({});
  const clean = f => f.replace(/ [^\w\s]/g, "").trim().replace("All", "All").replace("Photos", "Photos").replace("Videos", "Videos").replace("Stage Events", "Stage Events").replace("Candid", "Candid").replace("Group Photos", "Group Photos").replace("Teachers", "Teachers").replace("Reels", "Reels");
  const filtered = PHOTOS.filter(p => {
    const fc = clean(filter);
    const ok = fc === "All" || p.type === fc || (fc === "Photos" && p.type !== "Videos");
    const s = !search || p.label.toLowerCase().includes(search.toLowerCase()) || p.uploader.toLowerCase().includes(search.toLowerCase());
    return ok && s;
  });
  return (
    <div style={{ paddingTop: 80 }}>
      <div style={{ padding: "48px 24px 32px", maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 44 }}>
          <div className="divider"><span className="script" style={{ fontSize: 14, color: "var(--muted)", whiteSpace: "nowrap" }}>✦ ✦ ✦</span></div>
          <h1 className="script" style={{ fontSize: "clamp(38px,6vw,64px)", color: "var(--rose-d)", marginTop: 12 }}>Our Memory Gallery</h1>
          <p className="serif" style={{ color: "var(--muted)", fontStyle: "italic", marginTop: 10 }}>412 photographs · 4 years · A lifetime of love</p>
        </div>

        {/* Search */}
        <div style={{ maxWidth: 420, margin: "0 auto 28px", position: "relative" }}>
          <span style={{ position: "absolute", left: 16, top: "50%", transform: "translateY(-50%)", fontSize: 16 }}>🔍</span>
          <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search memories, names, events..." style={{ width: "100%", padding: "12px 20px 12px 44px", borderRadius: 50, border: "2px solid rgba(212,175,55,.2)", background: "#fff", color: "var(--text)", fontSize: 14, outline: "none", fontFamily: "'Nunito',sans-serif", boxShadow: "0 4px 16px rgba(0,0,0,.08)" }} />
        </div>

        {/* Filters */}
        <div style={{ display: "flex", gap: 10, marginBottom: 40, flexWrap: "wrap", justifyContent: "center" }}>
          {FILTERS.map((f, i) => (
            <button key={f} className={`chip ${filter === f ? "active" : ""}`} style={{ background: filter === f ? CHIP_COLORS[i] : "#fff", color: filter === f ? "#fff" : "var(--muted)", borderColor: filter === f ? "transparent" : CHIP_COLORS[i] + "40", boxShadow: filter === f ? `0 4px 16px ${CHIP_COLORS[i]}40` : "0 2px 8px rgba(62,32,48,.08)" }} onClick={() => setFilter(f)}>{f}</button>
          ))}
        </div>

        {/* Polaroid grid */}
        <div className="gallery-grid">
          {filtered.map((p, i) => (
            <div key={i} style={{ marginBottom: 24, breakInside: "avoid", isolation: "isolate", position: "relative" }}>
              <div className="polaroid" style={{ transform: `rotate(${ROTS[i % ROTS.length]}deg)` }} onClick={() => setLb(p)}>
                <div className="tape" style={{ background: TAPES[i % TAPES.length] }} />
                <div className="polaroid-img" style={{ height: 160 + (i % 3) * 40, background: p.image_path ? `url(${getImgUrl(p.image_path)}) center/cover` : p.grad, borderRadius: 1, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, opacity: p.image_path ? 1 : .55 }}>
                  {!p.image_path && "🖼️"}
                </div>
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: 2 }}>
                  <div className="polaroid-caption" style={{ textAlign: "left", fontSize: 13, flex: 1 }}>{p.label}</div>
                  <button onClick={e => { e.stopPropagation(); setLiked(l => ({ ...l, [i]: !l[i] })); }} style={{ background: "none", border: "none", cursor: "pointer", fontSize: 16, flexShrink: 0 }}>{liked[i] ? "❤️" : "🤍"}</button>
                </div>
                <div style={{ textAlign: "center", fontSize: 10, color: "var(--dim)", fontFamily: "'Nunito',sans-serif", marginTop: 2 }}>📷 {p.uploader} · {p.date}</div>
              </div>
            </div>
          ))}
        </div>
        {!filtered.length && <div style={{ textAlign: "center", padding: "80px 0", color: "var(--muted)" }}>
          <div style={{ fontSize: 48 }}>✨</div>
          <p className="serif" style={{ marginTop: 16, fontStyle: "italic" }}>No memories found. Try a different search.</p>
        </div>}
      </div>
    </div>
  );
}

// ─── VIDEOS ───────────────────────────────────────────────────────────────────
function Videos({ VIDEOS }) {
  const [hover, setHover] = useState(null);
  return (
    <div style={{ paddingTop: 80, minHeight: "100vh" }}>
      <div style={{ padding: "48px 24px", maxWidth: 1200, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 52 }}>
          <div className="divider"><span className="script" style={{ fontSize: 14, color: "var(--muted)", whiteSpace: "nowrap" }}>✦ ✦ ✦</span></div>
          <h1 className="script" style={{ fontSize: "clamp(36px,5.5vw,60px)", color: "var(--rose-d)", marginTop: 12 }}>Video Memories</h1>
          <p className="serif" style={{ color: "var(--muted)", fontStyle: "italic", marginTop: 10 }}>Every frame holds a feeling we never want to forget</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(320px,1fr))", gap: 28 }}>
          {VIDEOS.map((v, i) => (
            <div key={i} className="glass-card reveal" style={{ cursor: "pointer", borderRadius: 24, overflow: "hidden" }}
              onMouseEnter={() => setHover(i)} onMouseLeave={() => setHover(null)}
              onClick={() => v.video_path && window.open(getImgUrl(v.video_path), "_blank")}>
              <div style={{ height: 220, background: v.video_path ? "#000" : v.grad, position: "relative", display: "flex", alignItems: "center", justifyContent: "center" }}>
                {v.video_path ? (
                  <video src={getImgUrl(v.video_path)} style={{ width: "100%", height: "100%", objectFit: "cover", opacity: 0.6 }} muted />
                ) : null}
                <div className={hover === i ? "pulse-gold" : ""} style={{ position: "absolute", zIndex: 2, width: 68, height: 68, borderRadius: "50%", background: "rgba(255,255,255,.4)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26, transform: hover === i ? "scale(1.15)" : "scale(1)", transition: "transform .4s cubic-bezier(0.175, 0.885, 0.32, 1.275)", border: "2.5px solid rgba(255,255,255,0.6)", backdropFilter: "blur(6px)", boxShadow: "0 10px 30px rgba(0,0,0,0.15)" }}>▶</div>
                <div style={{ position: "absolute", bottom: 10, right: 14, fontSize: 12, background: "rgba(0,0,0,.45)", padding: "3px 10px", borderRadius: 20, color: "#fff", fontWeight: 600 }}>{v.duration}</div>
                <div style={{ position: "absolute", top: 10, left: 10, zIndex: 3 }}>
                  <span style={{ background: "rgba(255,255,255,.85)", padding: "3px 12px", borderRadius: 20, fontSize: 10, fontWeight: 700, color: "var(--brown)", letterSpacing: .5 }}>{v.type}</span>
                </div>
              </div>
              <div style={{ padding: "18px 20px 20px" }}>
                <div className="script" style={{ fontSize: 19, color: "var(--brown)", marginBottom: 8 }}>{v.title}</div>
                <div style={{ display: "flex", justifyContent: "space-between", color: "var(--muted)", fontSize: 13 }}>
                  <span>👁 {v.views} views</span><span>🎬 {v.video_path ? "Uploaded Video" : "Memory"}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── STUDENT WALL ─────────────────────────────────────────────────────────────
function StudentWall({ STUDENTS, setSelected }) {
  const [reacts, setReacts] = useState({});
  const rxns = ["🤍", "😭", "🔥", "🥹", "🤗", "✨"];
  return (
    <div style={{ paddingTop: 80, minHeight: "100vh" }}>
      <div style={{ padding: "48px 24px 80px", maxWidth: 1100, margin: "0 auto" }}>
        <div style={{ textAlign: "center", marginBottom: 52 }}>
          <div className="divider"><span className="script" style={{ fontSize: 14, color: "var(--muted)", whiteSpace: "nowrap" }}>✦ ✦ ✦</span></div>
          <h1 className="script" style={{ fontSize: "clamp(36px,5.5vw,60px)", color: "var(--rose-d)", marginTop: 14 }}>Student Wall of Memories</h1>
          <p className="serif" style={{ color: "var(--muted)", fontStyle: "italic", marginTop: 10 }}>Every voice of the Class of 2026 — written from the heart</p>
        </div>
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill,minmax(min(100%, 350px), 1fr))", gap: 32 }}>
          {STUDENTS.map((s, i) => (
            <div key={i} className="glass-card reveal" onClick={() => setSelected(s)} style={{ padding: "32px 28px", borderRadius: 28, position: "relative", overflow: "visible", cursor: "pointer" }}>
              <div style={{ position: "absolute", top: -20, right: 25, fontSize: 32, animation: "heartbeat 3s ease-in-out infinite", animationDelay: `${i * .4}s`, filter: "drop-shadow(0 5px 15px rgba(0,0,0,0.1))" }}>{s.emoji}</div>
              <div style={{ display: "flex", alignItems: "center", gap: 18, marginBottom: 24 }}>
                <div style={{ width: 64, height: 64, borderRadius: "50%", background: `linear-gradient(135deg,${s.color},var(--gold-d))`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 800, border: `4px solid #fff`, flexShrink: 0, color: "#fff", boxShadow: "var(--shadow-gold)" }}>{s.initials}</div>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 16, color: "var(--brown)" }}>{s.name}</div>
                  <div style={{ fontSize: 12, color: "var(--muted)", marginTop: 2 }}>{s.role}</div>
                </div>
              </div>
              <div style={{ background: `${s.color}08`, borderRadius: 14, padding: "16px 18px", marginBottom: 18, border: `1.5px solid ${s.color}15` }}>
                <p className="serif" style={{ color: "var(--brown)", lineHeight: 1.8, fontSize: 15, fontStyle: "italic" }}>"{s.quote.length > 120 ? s.quote.substring(0, 120) + "..." : s.quote}"</p>
              </div>
              <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                {rxns.map((r, ri) => (
                  <button key={ri} onClick={(e) => { e.stopPropagation(); setReacts(x => ({ ...x, [`${i}-${ri}`]: !x[`${i}-${ri}`] })) }} style={{ background: reacts[`${i}-${ri}`] ? `${s.color}20` : "#fff", border: `1.5px solid ${reacts[`${i}-${ri}`] ? s.color + "60" : "rgba(62,32,48,.1)"}`, borderRadius: 50, padding: "5px 12px", fontSize: 14, cursor: "pointer", transition: "all .2s", fontFamily: "'Nunito',sans-serif", boxShadow: "0 2px 6px rgba(62,32,48,.06)" }}>{r}</button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// ─── ADMIN LOGIN ─────────────────────────────────────────────────────────────
function AdminLogin({ onLogin }) {
  const [pass, setPass] = useState("");
  const handleLogin = (e) => {
    e.preventDefault();
    if (pass === "Admin@2026") onLogin();
    else alert("Incorrect Password");
  };
  return (
    <div style={{ paddingTop: 120, minHeight: "100vh", display: "flex", justifyContent: "center" }}>
      <div className="glass-card" style={{ width: 400, padding: 40, borderRadius: 32, textAlign: "center", height: "fit-content" }}>
        <div style={{ fontSize: 48, marginBottom: 20 }}>🔐</div>
        <h2 className="script" style={{ fontSize: 32, color: "var(--rose-d)", marginBottom: 10 }}>Admin Portal</h2>
        <p className="serif" style={{ fontSize: 14, color: "var(--muted)", marginBottom: 30 }}>Enter access code to manage memories</p>
        <form onSubmit={handleLogin}>
          <input
            type="password"
            value={pass}
            onChange={e => setPass(e.target.value)}
            placeholder="Access Code"
            style={{ width: "100%", padding: 14, borderRadius: 14, border: "2px solid rgba(212,175,55,0.2)", marginBottom: 20, outline: "none", textAlign: "center" }}
          />
          <button className="btn-rose" style={{ width: "100%", padding: 14, borderRadius: 14 }}>Unlock Dashboard</button>
        </form>
      </div>
    </div>
  );
}

// ─── UPLOAD / ADMIN DASHBOARD ────────────────────────────────────────────────
function AdminDashboard({ refreshData, PHOTOS, STUDENTS, VIDEOS, onLogout }) {
  const fileInputRef = useRef(null);
  const [caption, setCaption] = useState("");
  const [cat, setCat] = useState("Candid ✨");
  const [uploader, setUploader] = useState("");
  const [uploading, setUploading] = useState(false);
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);

  // Writing state
  const [wName, setWName] = useState("");
  const [wRole, setWRole] = useState("");
  const [wQuote, setWQuote] = useState("");
  const [wEmoji, setWEmoji] = useState("✨");

  const handleUpload = async () => {
    if (!caption || !uploader) return alert("Please fill all fields");
    setUploading(true);

    const isVideo = cat.includes("Videos") || cat.includes("Reels");
    const endpoint = isVideo ? "videos" : "memories";

    const formData = new FormData();
    if (isVideo) {
      formData.append("title", caption);
      formData.append("duration", "3:45"); // Default or could add an input
      formData.append("views", Math.floor(Math.random() * 500) + 100);
      formData.append("grad", "linear-gradient(135deg, #111, #444)");
      formData.append("type", cat.replace(/ [^\w\s]/g, "").trim());
      if (file) formData.append("video", file);
    } else {
      formData.append("label", caption);
      formData.append("type", cat.replace(/ [^\w\s]/g, "").trim());
      formData.append("uploader", uploader);
      formData.append("grad", "linear-gradient(135deg,#f8f9fa,#D4AF37)");
      if (file) formData.append("image", file);
    }

    try {
      const res = await fetch(`${API_BASE}/${endpoint}`, {
        method: "POST",
        body: formData
      });

      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (parseError) {
        console.error("Server Response:", text);
        throw new Error("Invalid JSON from server. Check console.");
      }

      if (data.success) {
        alert(`${isVideo ? "Video" : "Memory"} shared successfully! ✨`);
        setCaption("");
        setUploader("");
        setFile(null);
        refreshData();
      } else {
        alert("Upload failed: " + (data.error || "Unknown error"));
      }
    } catch (e) {
      console.error(e);
      alert("Upload failed: " + e.message);
    } finally {
      setUploading(false);
    }
  };

  const handleAddWriting = async () => {
    if (!wName || !wQuote) return alert("Please fill Name and Quote");
    setUploading(true);
    const formData = new FormData();
    formData.append("name", wName);
    formData.append("role", wRole || "Student");
    formData.append("quote", wQuote);
    formData.append("emoji", wEmoji);

    try {
      const res = await fetch(`${API_BASE}/students`, {
        method: "POST",
        body: formData
      });
      const text = await res.text();
      let data;
      try {
        data = JSON.parse(text);
      } catch (e) {
        console.error("Server Response:", text);
        throw new Error("Invalid response from server.");
      }

      if (data.success) {
        alert("Writing added! ✍️");
        setWName(""); setWRole(""); setWQuote("");
        refreshData();
      } else {
        alert("Failed to add writing: " + (data.error || "Unknown error"));
      }
    } catch (e) { console.error(e); }
    finally { setUploading(false); }
  };

  const deleteMemory = async (id, type) => {
    if (!window.confirm("Are you sure?")) return;
    try {
      let endpoint = "memories";
      if (type === 'writing') endpoint = "students";
      if (type === 'video') endpoint = "videos";

      const res = await fetch(`${API_BASE}/${endpoint}/${id}`, { method: "DELETE" });
      const data = await res.json();
      if (data.success) refreshData();
      else alert("Delete failed: " + (data.error || "Unknown error"));
    } catch (e) { console.error("Delete error:", e); }
  };

  const inputStyle = { width: "100%", padding: "11px 16px", borderRadius: 12, border: "2px solid rgba(212,175,55,.2)", background: "rgba(255,255,255,.08)", color: "var(--text)", fontSize: 14, outline: "none", fontFamily: "'Nunito',sans-serif", transition: "border-color .25s" };

  return (
    <div style={{ paddingTop: 80, minHeight: "100vh" }}>
      <div style={{ padding: "48px 24px 80px", maxWidth: 1100, margin: "0 auto" }}>

        {/* SECTION 1: PHOTO/VIDEO UPLOAD */}
        <div style={{ marginBottom: 36 }}>
          <span style={{ background: "rgba(212,175,55,.15)", border: "1.5px solid rgba(212,175,55,.3)", borderRadius: 50, padding: "5px 18px", fontSize: 12, fontWeight: 700, color: "var(--amber-d)", letterSpacing: .8, display: "inline-block", marginBottom: 12 }}>📸 STEP 1</span>
          <h1 className="script" style={{ fontSize: "clamp(36px,5vw,56px)", color: "var(--rose-d)" }}>Share Visual Memories</h1>
        </div>

        <div className="upload-grid" style={{ marginBottom: 80 }}>
          <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
            <div className={`drop-zone ${drag ? "over" : ""}`} onDragOver={e => { e.preventDefault(); setDrag(true); }} onDragLeave={() => setDrag(false)} onDrop={e => { e.preventDefault(); setDrag(false); if (e.dataTransfer.files[0]) setFile(e.dataTransfer.files[0]); }} onClick={() => fileInputRef.current?.click()}>
              <input type="file" ref={fileInputRef} hidden onChange={e => setFile(e.target.files[0])} />
              <div style={{ fontSize: 52, marginBottom: 14 }}>{file ? "📄" : "📷"}</div>
              <div className="script" style={{ fontSize: 22, color: "var(--rose-d)", marginBottom: 8 }}>{file ? file.name : "Choose Photo/Video"}</div>
              <p style={{ color: "var(--muted)", fontSize: 14, marginBottom: 24, lineHeight: 1.6 }}>Max Size: 2 GB</p>
            </div>
          </div>
          <div className="glass-card" style={{ borderRadius: 24, padding: "30px 28px", alignSelf: "start" }}>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 5 }}>CAPTION</label>
              <input value={caption} onChange={e => setCaption(e.target.value)} placeholder="Moment name..." style={inputStyle} />
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 5 }}>CATEGORY</label>
              <select value={cat} onChange={e => setCat(e.target.value)} style={inputStyle}>
                {["Photos 📸", "Videos 🎥", "Stage Events 💃", "Candid ✨", "Group Photos 👥", "Teachers 🎓", "Reels 🎬"].map(o => <option key={o}>{o}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 18 }}>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 5 }}>YOUR NAME</label>
              <input value={uploader} onChange={e => setUploader(e.target.value)} placeholder="Who's sharing?" style={inputStyle} />
            </div>
            <button className="btn-rose" onClick={handleUpload} disabled={uploading} style={{ width: "100%", padding: "13px", borderRadius: 14 }}>{uploading ? "⏳..." : "✨ Upload"}</button>
          </div>
        </div>

        {/* SECTION 2: MEMORY WRITING */}
        <div style={{ marginBottom: 36 }}>
          <span style={{ background: "rgba(212,175,55,.15)", border: "1.5px solid rgba(212,175,55,.3)", borderRadius: 50, padding: "5px 18px", fontSize: 12, fontWeight: 700, color: "var(--amber-d)", letterSpacing: .8, display: "inline-block", marginBottom: 12 }}>✍️ STEP 2</span>
          <h1 className="script" style={{ fontSize: "clamp(36px,5vw,56px)", color: "var(--rose-d)" }}>Memories Writing</h1>
        </div>

        <div className="glass-card" style={{ borderRadius: 28, padding: "40px", marginBottom: 80, maxWidth: 800, margin: "0 auto" }}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 5 }}>NAME</label>
              <input value={wName} onChange={e => setWName(e.target.value)} placeholder="Student Name" style={inputStyle} />
            </div>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 5 }}>BATCH/ROLE</label>
              <input value={wRole} onChange={e => setWRole(e.target.value)} placeholder="e.g. CSBS 2026" style={inputStyle} />
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 5 }}>YOUR MESSAGE</label>
            <textarea value={wQuote} onChange={e => setWQuote(e.target.value)} placeholder="Write your heart out..." style={{ ...inputStyle, height: 120, resize: "none" }} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20, marginBottom: 20 }}>
            <div>
              <label style={{ display: "block", fontSize: 11, fontWeight: 700, color: "var(--muted)", marginBottom: 5 }}>SYMBOL/EMOJI</label>
              <select value={wEmoji} onChange={e => setWEmoji(e.target.value)} style={inputStyle}>
                {["✨", "🎓", "🤍", "☕", "🌟", "🔥", "🫂", "📜"].map(e => <option key={e}>{e}</option>)}
              </select>
            </div>
            <button className="btn-rose" onClick={handleAddWriting} disabled={uploading} style={{ alignSelf: "end", padding: "13px", borderRadius: 14 }}>{uploading ? "⏳..." : "✍️ Add Writing"}</button>
          </div>
        </div>

        {/* MANAGEMENT TABLES */}
        <div style={{ display: "flex", flexDirection: "column", gap: 40 }}>
          <div className="glass-card" style={{ borderRadius: 24, padding: "32px", overflowX: "auto" }}>
            <div className="script" style={{ fontSize: 24, color: "var(--brown)", marginBottom: 20 }}>📸 Gallery Management</div>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid rgba(212,175,55,.1)", color: "var(--muted)", fontWeight: 800 }}>
                  <th style={{ padding: 12 }}>CAPTION</th>
                  <th style={{ padding: 12 }}>TYPE</th>
                  <th style={{ padding: 12 }}>PATH</th>
                  <th style={{ padding: 12 }}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {PHOTOS.map((p, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid rgba(0,0,0,.05)" }}>
                    <td style={{ padding: 12, fontWeight: 600 }}>{p.label}</td>
                    <td style={{ padding: 12 }}>{p.type}</td>
                    <td style={{ padding: 12, fontSize: 10, color: "var(--muted)" }}>{p.image_path || "No Image"}</td>
                    <td style={{ padding: 12 }}><button onClick={() => deleteMemory(p.id, 'memory')} style={{ color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontSize: 11 }}>DELETE</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="glass-card" style={{ borderRadius: 24, padding: "32px", overflowX: "auto" }}>
            <div className="script" style={{ fontSize: 24, color: "var(--brown)", marginBottom: 20 }}>🎬 Video Management</div>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid rgba(212,175,55,.1)", color: "var(--muted)", fontWeight: 800 }}>
                  <th style={{ padding: 12 }}>TITLE</th>
                  <th style={{ padding: 12 }}>TYPE</th>
                  <th style={{ padding: 12 }}>PATH</th>
                  <th style={{ padding: 12 }}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {VIDEOS.map((v, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid rgba(0,0,0,.05)" }}>
                    <td style={{ padding: 12, fontWeight: 600 }}>{v.title}</td>
                    <td style={{ padding: 12 }}>{v.type}</td>
                    <td style={{ padding: 12, fontSize: 10, color: "var(--muted)" }}>{v.video_path || "No File"}</td>
                    <td style={{ padding: 12 }}><button onClick={() => deleteMemory(v.id, 'video')} style={{ color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontSize: 11 }}>DELETE</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <div className="glass-card" style={{ borderRadius: 24, padding: "32px", overflowX: "auto" }}>
            <div className="script" style={{ fontSize: 24, color: "var(--brown)", marginBottom: 20 }}>✍️ Writings Management</div>
            <table style={{ width: "100%", borderCollapse: "collapse", textAlign: "left", fontSize: 13 }}>
              <thead>
                <tr style={{ borderBottom: "2px solid rgba(212,175,55,.1)", color: "var(--muted)", fontWeight: 800 }}>
                  <th style={{ padding: 12 }}>NAME</th>
                  <th style={{ padding: 12 }}>MESSAGE</th>
                  <th style={{ padding: 12 }}>ACTION</th>
                </tr>
              </thead>
              <tbody>
                {STUDENTS.map((s, i) => (
                  <tr key={i} style={{ borderBottom: "1px solid rgba(0,0,0,.05)" }}>
                    <td style={{ padding: 12, fontWeight: 600 }}>{s.name}</td>
                    <td style={{ padding: 12, maxWidth: 300, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{s.quote}</td>
                    <td style={{ padding: 12 }}><button onClick={() => deleteMemory(s.id, 'writing')} style={{ color: "#ef4444", background: "none", border: "none", cursor: "pointer", fontSize: 11 }}>DELETE</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── FOOTER ───────────────────────────────────────────────────────────────────
function Footer({ page, setPage }) {
  return (
    <footer style={{ padding: "52px 24px 36px", borderTop: "1.5px solid rgba(212,175,55,.15)", textAlign: "center", background: "linear-gradient(135deg,rgba(212,175,55,.05),rgba(212,175,55,.05))" }}>
      <div style={{ fontSize: 36, marginBottom: 10, animation: "heartbeat 2.5s infinite" }}>CSBS family</div>
      <div className="script" style={{ fontSize: 32, color: "var(--rose-d)", marginBottom: 8 }}>CSBS Farewell 2026</div>
      <p className="serif" style={{ color: "var(--muted)", fontSize: 14, fontStyle: "italic", marginBottom: 24 }}>Computer Science Business Systems · Batch 2022–2026</p>
      <div style={{ display: "flex", justifyContent: "center", gap: 20, flexWrap: "wrap", marginBottom: 24 }}>
        {[["home", "Home"], ["gallery", "Gallery"], ["videos", "Videos"], ["wall", "Students"]].map(([id, l]) => (
          <button key={id} className="nav-pill" onClick={() => setPage(id)} style={{ fontWeight: 600 }}>{l}</button>
        ))}
      </div>
      <div className="divider" style={{ maxWidth: 360, margin: "0 auto 16px" }}><span className="script" style={{ fontSize: 13, color: "var(--dim)", whiteSpace: "nowrap" }}>✦ ✦ ✦</span></div>
      <p className="serif" style={{ color: "var(--dim)", fontSize: 13, fontStyle: "italic" }}>"Moments end, memories stay forever." · Developed by Rashid</p>
    </footer>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
export default function App() {
  const [page, setPage] = useState("home");
  const [isAdmin, setIsAdmin] = useState(false);
  const [photos, setPhotos] = useState([]);
  const [students, setStudents] = useState([]);
  const [videos, setVideos] = useState([]);
  const [faculty, setFaculty] = useState([]);
  const [loading, setLoading] = useState(true);
  const [lb, setLb] = useState(null);
  const [selectedStudent, setSelectedStudent] = useState(null);

  const fetchData = async () => {
    const fetchRes = async (endpoint) => {
      try {
        const res = await fetch(`${API_BASE}/${endpoint}`);
        const text = await res.text();
        try {
          return JSON.parse(text);
        } catch (e) {
          console.error(`Malformed JSON from ${endpoint}:`, text);
          return [];
        }
      } catch (e) {
        console.error(`Fetch failed for ${endpoint}:`, e);
        return [];
      }
    };

    try {
      const [p, s, v, f] = await Promise.all([
        fetchRes("memories"),
        fetchRes("students"),
        fetchRes("videos"),
        fetchRes("faculty")
      ]);
      setPhotos(p);
      setStudents(s);
      setVideos(v);
      setFaculty(f);
    } catch (e) {
      console.error("FetchData overall error:", e);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, []);
  useEffect(() => { window.scrollTo({ top: 0, behavior: "smooth" }); }, [page]);

  const pages = {
    home: <Home setPage={setPage} PHOTOS={photos} FACULTY={faculty} VIDEOS={videos} STUDENTS={students} />,
    gallery: <Gallery PHOTOS={photos} setLb={setLb} />,
    videos: <Videos VIDEOS={videos} />,
    wall: <StudentWall STUDENTS={students} setSelected={setSelectedStudent} />,
    login: <AdminLogin onLogin={() => { setIsAdmin(true); setPage("admin"); }} />,
    admin: isAdmin ? <AdminDashboard refreshData={fetchData} PHOTOS={photos} STUDENTS={students} VIDEOS={videos} onLogout={() => { setIsAdmin(false); setPage("home"); }} /> : <AdminLogin onLogin={() => { setIsAdmin(true); setPage("admin"); }} />
  };

  return (
    <>
      <style>{FONTS + CSS}</style>
      {loading ? <Preloader /> : (
        <>
          <Hearts />
          <div style={{ position: "relative", zIndex: 1 }}>
            <Nav page={page} setPage={setPage} isAdmin={isAdmin} />
            <main>{pages[page] || <Home setPage={setPage} />}</main>
            <Footer page={page} setPage={setPage} />
          </div>
          {lb && <Lightbox photo={lb} onClose={() => setLb(null)} />}
          {selectedStudent && <StudentModal student={selectedStudent} onClose={() => setSelectedStudent(null)} />}
        </>
      )}
    </>
  );
}